import React, { useEffect, useMemo, useRef, useState } from 'react';
import './App.css';
import { mapPubResponseToViewModel, getImageDocuments } from './mapPubResponse';
import { fetchPubPayload, getPublicIdFromLocation, readEmbeddedPubPayload } from './pubApi';
import { ThumbnailImage } from './ThumbnailImage';
import { canSubmitContactForm, getContactFormConfig } from './contactForm';
import { RequestServiceModal } from './RequestServiceModal';
import { DeviceHelpChat } from './DeviceHelpChat';
import { detectLocale, getTranslator, SUPPORTED_LOCALES } from './i18n';

// --- Icons ---
const GridDotsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="5" cy="5" r="1.5"/><circle cx="12" cy="5" r="1.5"/><circle cx="19" cy="5" r="1.5"/>
    <circle cx="5" cy="12" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="19" cy="12" r="1.5"/>
    <circle cx="5" cy="19" r="1.5"/><circle cx="12" cy="19" r="1.5"/><circle cx="19" cy="19" r="1.5"/>
  </svg>
);

const DocIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <polyline points="14 2 14 8 20 8"></polyline>
  </svg>
);

const WrenchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
  </svg>
);

const GridIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="7" height="7"></rect>
    <rect x="14" y="3" width="7" height="7"></rect>
    <rect x="14" y="14" width="7" height="7"></rect>
    <rect x="3" y="14" width="7" height="7"></rect>
  </svg>
);

const ListIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="8" y1="6" x2="21" y2="6"></line>
    <line x1="8" y1="12" x2="21" y2="12"></line>
    <line x1="8" y1="18" x2="21" y2="18"></line>
    <line x1="3" y1="6" x2="3.01" y2="6"></line>
    <line x1="3" y1="12" x2="3.01" y2="12"></line>
    <line x1="3" y1="18" x2="3.01" y2="18"></line>
  </svg>
);

const DropdownArrowIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

function statusPillClass(status) {
  if (typeof status !== 'string') return 'status-value';
  const s = status.toLowerCase();
  if (s.includes('required') || s.includes('overdue') || s.includes('critical')) return 'status-value red';
  return 'status-value';
}

const LOCALE_STORAGE_KEY = 'healtek.portal.locale';

const LANGUAGE_LABELS = {
  en: '🇬🇧\u00A0\u00A0English',
  cs: '🇨🇿\u00A0\u00A0Čeština',
  de: '🇩🇪\u00A0\u00A0Deutsch',
};

function Header({ locale, onLocaleChange, t }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  return (
    <header className="app-header">
      <div className="header-left">
        <img
          src={process.env.PUBLIC_URL + '/logo-healtek.png'}
          alt="Healtek Logo"
          className="logo-img"
        />
        <button type="button" className="icon-btn"><GridDotsIcon /></button>
        <div className="header-divider"></div>
      </div>
      <div className="header-right">
        <label className="language-switcher-label" htmlFor="language-switcher">
          {t('app.language')}
        </label>
        <div className="language-switcher" ref={menuRef}>
          <button
            id="language-switcher"
            type="button"
            className="language-switcher-trigger"
            onClick={() => setOpen((v) => !v)}
            aria-haspopup="listbox"
            aria-expanded={open}
          >
            <span>{LANGUAGE_LABELS[locale]}</span>
            <span className="language-switcher-caret"><DropdownArrowIcon /></span>
          </button>
          {open && (
            <div className="language-switcher-menu" role="listbox" aria-label={t('app.language')}>
              {SUPPORTED_LOCALES.map((lang) => (
                <button
                  key={lang}
                  type="button"
                  className={`language-switcher-option ${lang === locale ? 'active' : ''}`}
                  onClick={() => {
                    onLocaleChange(lang);
                    setOpen(false);
                  }}
                >
                  {LANGUAGE_LABELS[lang]}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

/** Set false to re-enable “Request service” after contact-form deploy is verified. */
const REQUEST_SERVICE_BUTTON_DISABLED_TEMP = false;

function AssetDetail({ viewModel: vm, payload, locale, t }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const contactConfig = useMemo(() => getContactFormConfig(payload), [payload]);
  const requestServiceActive = canSubmitContactForm(contactConfig);
  const imageDocs = useMemo(() => getImageDocuments(payload), [payload]);
  const allDocs = Array.isArray(payload?.documents) ? payload.documents : [];

  /** Prefer main product picture doc, else first image MIME row from API. */
  const heroImageDoc = useMemo(() => {
    if (!imageDocs.length) return null;
    return (
      imageDocs.find(
        (d) =>
          typeof d.documentCategory === 'string' &&
          d.documentCategory.toLowerCase().includes('productpicture')
      ) || imageDocs[0]
    );
  }, [imageDocs]);

  const tabs = useMemo(
    () => [
      { key: 'overview', label: t('tabs.overview') },
      { key: 'serviceHistory', label: t('tabs.serviceHistory') },
      { key: 'documents', label: t('tabs.documents') },
      { key: 'gallery', label: t('tabs.gallery') },
    ],
    [t]
  );

  return (
    <div className="asset-card">
      <div className="card-header">
        <div className="header-top-row">
          <div className="cert-title-row">
            <span className="cert-icon"><DocIcon /></span>
            <div>
              {vm.heading && <div className="cert-title">{vm.heading}</div>}
              {vm.certificateId && <div className="cert-id">{vm.certificateId}</div>}
            </div>
          </div>
          <div className="view-toggles">
            <button type="button" className="view-btn"><GridIcon /></button>
            <button type="button" className="view-btn active"><ListIcon /></button>
          </div>
        </div>

        <div className="custom-tabs-container">
          <div className="custom-tab-list">
            {tabs.map((tab) => (
              <div
                key={tab.key}
                className={`custom-tab-item ${activeTab === tab.key ? 'is-selected' : ''}`}
                onClick={() => setActiveTab(tab.key)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setActiveTab(tab.key);
                }}
              >
                {tab.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card-body">
        {activeTab === 'overview' && (
          <>
            <div className="content-grid">
              <div className="info-col">
                {vm.productName && <h2 className="product-name">{vm.productName}</h2>}
                {/* {vm.categoryLabel && (
                  <p className="product-category">{vm.categoryLabel}</p>
                )} */}

                {(vm.productCode || vm.serialNumber) && (
                  <div className="meta-row">
                    {vm.productCode && (
                      <div className="meta-item">
                        <span className="meta-label">{t('app.productCode')}</span>
                        <span className="meta-value link-style">{vm.productCode}</span>
                      </div>
                    )}
                    {vm.serialNumber && (
                      <div className="meta-item">
                        <span className="meta-label">{t('app.serialNumber')}</span>
                        <span className="meta-value link-style">{vm.serialNumber}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* {vm.extraComponents?.filter(
                  (c) => c && (c.productName || c.productCode || c.serialNumber)
                ).length > 0 && (
                  <div className="extra-components">
                    <h3 className="extra-components-title">Additional items</h3>
                    <ul className="extra-components-list">
                      {vm.extraComponents
                        .filter(
                          (c) =>
                            c && (c.productName || c.productCode || c.serialNumber)
                        )
                        .map((c, i) => (
                          <li
                            key={
                              c.itemId != null
                                ? String(c.itemId)
                                : `extra-${i}`
                            }
                          >
                            {c.productName && <strong>{c.productName}</strong>}
                            {(c.productCode || c.serialNumber) && (
                              <span className="extra-meta">
                                {[c.productCode, c.serialNumber]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </span>
                            )}
                          </li>
                        ))}
                    </ul>
                  </div>
                )} */}

                {vm.description && <p className="product-desc">{vm.description}</p>}

                {(vm.status || vm.nextPmDate || vm.lastServiced) && (
                  <div className="status-row">
                    {vm.status && (
                      <div className="status-col">
                        <span className="status-label">{t('app.status')}</span>
                        <span className={statusPillClass(vm.status)}>{vm.status}</span>
                      </div>
                    )}
                    {vm.nextPmDate && (
                      <div className="status-col">
                        <span className="status-label">{t('app.nextPmDueDate')}</span>
                        <span className="status-value">{vm.nextPmDate}</span>
                      </div>
                    )}
                    {vm.lastServiced && (
                      <div className="status-col">
                        <span className="status-label">{t('app.lastServiced')}</span>
                        <span className="status-value">{vm.lastServiced}</span>
                      </div>
                    )}
                  </div>
                )}

                <button
                  type="button"
                  className="btn-primary"
                  disabled={REQUEST_SERVICE_BUTTON_DISABLED_TEMP || !requestServiceActive}
                  onClick={() => setRequestModalOpen(true)}
                >
                  <WrenchIcon /> {t('app.requestService')}
                </button>
              </div>

              {heroImageDoc && (
                <div className="image-col">
                  <ThumbnailImage
                    doc={heroImageDoc}
                    payload={payload}
                    preset="gallery_thumb"
                    alt={vm.productName || ''}
                    className="asset-image"
                  />
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'serviceHistory' && null}

        {activeTab === 'documents' && (
          <ul className="doc-list doc-list-with-thumbs">
            {allDocs
              .filter(
                (d) =>
                  d &&
                  (d.title || d.documentType || d.documentCategoryName)
              )
              .map((d, i) => (
                <li
                  key={d.documentId != null ? String(d.documentId) : `doc-${i}`}
                  className="doc-list-row"
                >
                  <div className="doc-thumb-wrap">
                    <ThumbnailImage
                      doc={d}
                      payload={payload}
                      preset="document_thumb"
                      alt=""
                      className="doc-thumb"
                    />
                  </div>
                  <div className="doc-list-text">
                    {d.title && <span className="doc-title">{d.title}</span>}
                    {!d.title && d.documentType && (
                      <span className="doc-title">{d.documentType}</span>
                    )}
                    {d.documentCategoryName && (
                      <span className="doc-cat">{d.documentCategoryName}</span>
                    )}
                  </div>
                </li>
              ))}
          </ul>
        )}

        {activeTab === 'gallery' && (
          <div className="gallery-tab thumb-grid">
            {imageDocs.map((d) => (
              <figure key={d.documentId} className="thumb-cell">
                <ThumbnailImage
                  doc={d}
                  payload={payload}
                  preset="gallery_thumb"
                  alt={d.title || ''}
                  className="thumb-img"
                />
                {d.title && (
                  <figcaption className="thumb-caption">{d.title}</figcaption>
                )}
              </figure>
            ))}
          </div>
        )}
      </div>

      <DeviceHelpChat
        payload={payload}
        locale={locale}
        t={t}
        onRequestService={() => setRequestModalOpen(true)}
      />

      <RequestServiceModal
        open={requestModalOpen}
        onClose={() => setRequestModalOpen(false)}
        contactConfig={contactConfig}
        viewModel={vm}
        t={t}
      />
    </div>
  );
}

function App() {
  const [locale, setLocale] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = window.localStorage.getItem(LOCALE_STORAGE_KEY);
      if (saved && SUPPORTED_LOCALES.includes(saved)) return saved;
    }
    return detectLocale();
  });
  const t = useMemo(() => getTranslator(locale), [locale]);
  const publicId = useMemo(() => getPublicIdFromLocation(), []);
  const embedded = useMemo(() => readEmbeddedPubPayload(), []);

  const [phase, setPhase] = useState(() => {
    if (embedded) return 'ready';
    if (publicId) return 'loading';
    return 'idle';
  });
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(() => embedded || null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  }, [locale]);

  useEffect(() => {
    if (embedded) return;
    if (!publicId) return;
    let cancelled = false;
    (async () => {
      setPhase('loading');
      setError(null);
      try {
        const { res, data } = await fetchPubPayload(publicId);
        if (cancelled) return;
        if (!res.ok) {
          const msg =
            data?.error ||
            data?.detail ||
            `${t('app.requestFailed')} (${res.status})`;
          setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
          setPayload(null);
          setPhase('error');
          return;
        }
        if (data && data.ok === false) {
          setError(data.error || t('app.unknownError'));
          setPayload(data);
          setPhase('error');
          return;
        }
        setPayload(data);
        setPhase('ready');
      } catch (e) {
        if (cancelled) return;
        setError(e?.message || String(e));
        setPayload(null);
        setPhase('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [publicId, embedded, t]);

  const viewModel = useMemo(() => mapPubResponseToViewModel(payload), [payload]);

  return (
    <div className="app-container">
      <Header locale={locale} onLocaleChange={setLocale} t={t} />
      <main className="main-layout">
        {!publicId && !embedded && (
          <div className="pub-state-card">
            <p>{t('app.missingPublicId')}</p>
            <p className="pub-hint">
              {t('app.missingPublicIdHintPrefix')} <code>/pub/&lt;id_public&gt;</code>, {t('app.missingPublicIdHintMiddle')}{' '}
              <code>?pubId=&lt;uuid&gt;</code> {t('app.missingPublicIdHintSuffix')}
            </p>
          </div>
        )}
        {publicId && !embedded && phase === 'loading' && (
          <div className="pub-state-card">{t('app.loadingCertificate')}</div>
        )}
        {publicId && phase === 'error' && (
          <div className="pub-state-card pub-error">
            <p><strong>{t('app.couldNotLoadCertificate')}</strong></p>
            <p>{error}</p>
          </div>
        )}
        {phase === 'ready' && viewModel && (
          <AssetDetail viewModel={viewModel} payload={payload} locale={locale} t={t} />
        )}
        {phase === 'ready' && !viewModel && (publicId || embedded) && (
          <div className="pub-state-card pub-error">
            <p>{t('app.unexpectedResponseShape')}</p>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
